import { useEffect, useState } from 'react';
import Api, { ApiError } from '../../../../../data/api/Api';
import { Box, Modal, Typography, Select, MenuItem, FormControl, InputLabel, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import { ListOfExistingDataset } from '../createDataset/DatasetModal';
import { showSnackbarErrorMessage } from '../../../../../components/staticComponents/staticUtiles';

interface ViewAllModalProps {
  viewAllModalOpen: boolean;
  closeViewAllModal: () => void;
  currentDatasetId: string;
}

interface FileStatus {
  file_name: string;
  status: string;
}

interface DatasetFileStatusResponse {
  dataset_file_statuses: FileStatus[];
}

const ViewDatasetModal = ({ viewAllModalOpen, closeViewAllModal, currentDatasetId }: ViewAllModalProps) => {
  const { t } = useTranslation();
  const [datasetDetails, setDatasetDetails] = useState<ListOfExistingDataset | null>(null);
  const [datasetFileStatuses, setDatasetFileStatuses] = useState<FileStatus[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<string>('All');
  const [loading, setLoading] = useState<boolean>(false);

  const getDatasetDetails = async () => {
    try {
      const datasetDetail = await Api.getDataset(currentDatasetId);
      setDatasetDetails(datasetDetail.data as ListOfExistingDataset);
    } catch (error) {
      showSnackbarErrorMessage(error as ApiError);
    }
  };

  const getDatasetFileStatuses = async (statusFilter?: string) => {
    try {
      setLoading(true);
      console.log('Fetching file statuses for dataset:', currentDatasetId, 'with filter:', statusFilter);
      
      // Get statuses with optional server-side filtering
      const fileStatusResponse = await Api.datasetFileStatus(
        currentDatasetId, 
        statusFilter && statusFilter !== 'All' ? statusFilter : undefined
      );
      
      console.log('File status response:', fileStatusResponse);
      
      // Extract the dataset_file_statuses array from the response
      if (fileStatusResponse.data) {
        const responseData = fileStatusResponse.data as DatasetFileStatusResponse;
        if (responseData.dataset_file_statuses) {
          const fileStatuses = responseData.dataset_file_statuses;
          setDatasetFileStatuses(Array.isArray(fileStatuses) ? fileStatuses : []);
        } else {
          setDatasetFileStatuses([]);
        }
      } else if (fileStatusResponse.response?.ok) {
        // If response is ok but no data, set empty array
        setDatasetFileStatuses([]);
      } else {
        console.warn('Unexpected response structure:', fileStatusResponse);
        setDatasetFileStatuses([]);
      }
    } catch (error) {
      console.error('Detailed error fetching file statuses:', error);
      
      // Check if it's a network error
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        console.error('Network/CORS error - the API endpoint may not exist or server is unavailable');
      } else {
        // Show user-facing error for other types of errors
        const apiError = error as ApiError;
        if (apiError.status && apiError.status !== 0) {
          showSnackbarErrorMessage(apiError);
        }
      }
      
      setDatasetFileStatuses([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (viewAllModalOpen && currentDatasetId) {
      getDatasetDetails();
      getDatasetFileStatuses();
    }
  }, [viewAllModalOpen, currentDatasetId]);

  const filteredDatasetFileStatuses = selectedStatus === 'All' 
    ? datasetFileStatuses 
    : datasetFileStatuses.filter((file: FileStatus) => file.status === selectedStatus);

  const handleStatusChange = (event: any) => {
    const newStatus = event.target.value;
    setSelectedStatus(newStatus);
    
    // Optionally, we could re-fetch data with server-side filtering
    // For now, we'll use client-side filtering for better UX
    // Uncomment below to use server-side filtering:
    // if (newStatus === 'All') {
    //   getDatasetFileStatuses();
    // } else {
    //   getDatasetFileStatusesByStatus(newStatus);
    // }
  };

  return (
    <Modal
      open={viewAllModalOpen}
      onClose={closeViewAllModal}
      aria-labelledby="dataset-modal-title"
      aria-describedby="dataset-modal-description"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: '80%', md: '600px' },
          maxWidth: '600px',
          bgcolor: 'background.paper',
          boxShadow: 24,
          padding: 4,
        }}
      >
        <Box>
          <CloseIcon sx={{ float: 'right', cursor: 'pointer' }} onClick={closeViewAllModal} />
        </Box>
        {!datasetDetails ? (
          <Typography>{t('viewModal.loadingDatasetDetails')}</Typography>
        ) : (
          <>
            
            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
              <Typography sx={{ fontWeight: 700 }}>{t('fileUpload.datasetContent')}</Typography>
              <FormControl sx={{ marginTop: '22px', marginBottom: '16px', width: '250px' }}>
                <InputLabel id="status-select-label">Filter by Status</InputLabel>
                <Select
                  labelId="status-select-label"
                  value={selectedStatus}
                  label="Filter by Status"
                  onChange={handleStatusChange}
                  size="small"
                >
                  <MenuItem value="All">All</MenuItem>
                  <MenuItem value="Succeeded">Succeeded</MenuItem>
                  <MenuItem value="Failed">Failed</MenuItem>
                  <MenuItem value="Indexing">Indexing</MenuItem>
                  <MenuItem value="Queued">Queued</MenuItem>
                </Select>
              </FormControl>
            </Box>
            
            {/* {datasetDetails.files && datasetDetails.files.length > 0 ? (
                datasetDetails.files.map((file: ListOfExistingDataset, index: number) => (
                  <Typography
                    key={index}
                    padding={'.25rem .25rem'}
                    bgcolor={'#F6F6F6B2'}
                    borderRadius={'.25rem'}
                    mb={1}
                  >
                    {file.name}
                  </Typography>
                ))
              ) : (
                <Typography>{t('viewModal.No_files_available')}</Typography>
              )} */}
            <Box
              id="dataset-modal-description"
              mt={2}
              sx={{
                maxHeight: '300px',
                overflowY: 'auto',
                width: '100%'
              }}
            >
              {loading ? (
                <Typography align="center" sx={{ py: 4 }}>Loading file statuses...</Typography>
              ) : filteredDatasetFileStatuses && filteredDatasetFileStatuses.length > 0 ? (
                <TableContainer 
                  component={Paper} 
                  sx={{ 
                    boxShadow: 'none', 
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    width: 'calc(100% - 2px)',
                    margin: '0 auto'
                  }}
                >
                  <Table 
                    size="small" 
                    aria-label="dataset files table"
                    sx={{ 
                      tableLayout: 'fixed',
                      width: '100%'
                    }}
                  >
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                        <TableCell 
                          sx={{ 
                            fontWeight: 600, 
                            fontSize: '0.875rem',
                            width: 'auto'
                          }}
                        >
                          File Name
                        </TableCell>
                        <TableCell 
                          sx={{ 
                            fontWeight: 600, 
                            fontSize: '0.875rem', 
                            width: '120px',
                            minWidth: '120px'
                          }} 
                          align="center"
                        >
                          Status
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredDatasetFileStatuses.map((file: FileStatus, index: number) => (
                        <TableRow 
                          key={index}
                          sx={{ 
                            '&:last-child td, &:last-child th': { border: 0 },
                            '&:hover': { bgcolor: '#f9f9f9' }
                          }}
                        >
                          <TableCell 
                            component="th" 
                            scope="row"
                            sx={{ 
                              fontSize: '0.875rem',
                              wordBreak: 'break-word',
                              whiteSpace: 'normal',
                              padding: '8px 12px',
                              maxWidth: '0',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                          >
                            {file.file_name}
                          </TableCell>
                          <TableCell 
                            align="center"
                            sx={{
                              color: file.status === 'Succeeded' ? '#4caf50' : 
                                     file.status === 'Indexing' ? '#ff9800' : '#f44336',
                              fontWeight: 600,
                              fontSize: '0.875rem',
                              whiteSpace: 'nowrap',
                              padding: '8px 12px',
                              width: '120px',
                              minWidth: '120px'
                            }}
                          >
                            {file.status}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  {datasetFileStatuses.length === 0 && !loading 
                    ? 'No file status information available. The API endpoint may not be accessible.'
                    : t('viewModal.No_files_available')
                  }
                </Typography>
              )}
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default ViewDatasetModal;
